import { SortOptions } from "~/bff/types/SortOptions";
import { SortOrderTypes } from "~/constants/data-layer";
import { encodeFiltersHash } from "~/helpers/encode-filters-hash";
import { getFilterValuesForRequest } from "~/helpers/get-filter-values-for-request";
import { pushHistoryState } from "~/helpers/history";
import { removeQueryParam } from "~/helpers/slug";
import { Nullable } from "~/types/general.types";
import { IHashFilterParams } from "~/types/products-request-handler";

import { isStoreOnlyDropdownQuery } from "../products-layout/components/products-filters/components/filter/components/shop-filter/helpers/is-store-only-dropdown-query";
import { AppliedFilters } from "../products-layout/components/products-filters/types";
import { DEFAULT_PAGE_SIZE } from "../products-layout/constants";

export interface ApplyFunctions {
  applyShop: () => void;
  applyFilters: (f: AppliedFilters) => void;
  applySorting: (s: string) => void;
}

export const getPageNumber = (pageQueryParam: string | string[]): number => {
  const value = Array.isArray(pageQueryParam)
    ? Number(pageQueryParam[0])
    : Number(pageQueryParam);
  return value >= 1 ? value : 1;
};

const updateHashAndPreservePageScrollPos = (hash: string) => {
  // The solution is to remove empty hash with # from url
  // or prevent empty hash to be added if there is no hash in url

  if (!hash || hash === "#") {
    if (location.hash) {
      pushHistoryState(location.pathname + location.search);
    }
    return;
  }

  if (hash?.startsWith("#")) {
    pushHistoryState(location.pathname + location.search + hash);
    return;
  }

  /* eslint-disable no-restricted-globals */
  const topPos =
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0;

  const leftPos =
    window.pageXOffset ||
    document.documentElement.scrollLeft ||
    document.body.scrollLeft ||
    0;

  location.hash = hash;

  document.documentElement.scrollTop = topPos;
  document.documentElement.scrollLeft = leftPos;
  /* eslint-enable */
};

export const updateUrlHash: (props: IHashFilterParams) => void = ({
  activeSortingParam = "",
  appliedFilters = null,
  isShopFilter = false,
  selectedStoreId = "",
  productType,
  config: {
    getMoreItems,
    variables: {
      q,
      efq,
      locale = "en-GB",
      categorySlug = null,
      shouldConcat = false,
      start = 0,
      rows = DEFAULT_PAGE_SIZE,
      brUid2,
      brRefUrl,
    },
  },
}) => {
  const hash = encodeFiltersHash(
    activeSortingParam,
    appliedFilters,
    isShopFilter,
    productType,
  );

  const isRangedStoresFacet = isStoreOnlyDropdownQuery(efq);
  updateHashAndPreservePageScrollPos(hash);

  getMoreItems({
    variables: {
      q: q ?? "",
      efq,
      start,
      rows,
      locale,
      categorySlug: removeQueryParam(categorySlug),
      fq:
        appliedFilters || isShopFilter !== null
          ? getFilterValuesForRequest(
              appliedFilters,
              isShopFilter,
              selectedStoreId,
              isRangedStoresFacet,
            )
          : null,
      shouldConcat,
      sort: activeSortingParam,
      brUid2,
      brRefUrl,
    },
  });
};

export const getSortingParams = (
  sortOptions: SortOptions,
): { value: string; text: Nullable<string> | undefined }[] => {
  return [
    {
      value: "",
      text: sortOptions?.props?.translations?.relevance,
    },
    {
      value: "startDate desc",
      text: sortOptions?.props?.translations?.newest,
    },
    {
      value: "low_price asc",
      text: sortOptions?.props?.translations?.priceLowHigh,
    },
    {
      value: "low_price desc",
      text: sortOptions?.props?.translations?.priceHighLow,
    },
  ];
};

export const getSortOrderForGA = (sortOption: string): string | undefined => {
  switch (sortOption) {
    case "startDate desc":
      return SortOrderTypes.newest;
    case "":
      return SortOrderTypes.relevance;
    case "low_price asc":
      return SortOrderTypes.priceLowToHigh;
    case "low_price desc":
      return SortOrderTypes.priceHighToLow;
    default:
      return undefined;
  }
};
